import { useSearch } from "@tanstack/react-router";
import { useEffect } from "react";
import { Autocomplete, useFilter } from "react-aria-components";
import { Controller } from "react-hook-form";

import { FieldError, Input, Label } from "@/components/field";
import { SearchField } from "@/components/search-field";
import {
  Select,
  SelectButton,
  SelectClearButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";
import { Text } from "@/components/text";
import { useGetBrandsByProjectId } from "@/features/brands/shared/hooks/useGetBrandsByProjectId";
import { useGetCurrentProjectId } from "@/hooks/useGetCurrentProjectId";

import { useDefaultRestrictAiAssetsFromBrand } from "../../../shared/useDefaultRestrictAiAssetsFromBrand";
import { useCreateGraphicRequestFormContext } from "../useCreateGraphicRequestForm";

export function BrandsSelect() {
  const { control, setValue } = useCreateGraphicRequestFormContext();
  const projectId = useGetCurrentProjectId();
  const { data: brands, isLoading } = useGetBrandsByProjectId(projectId);
  const { contains } = useFilter({ sensitivity: "base" });

  useDefaultRestrictAiAssetsFromBrand("brand", brands);

  const { brand_id } = useSearch({
    from: "/_auth/_app-shell/requests/create",
  });

  useEffect(() => {
    if (brand_id) {
      setValue("brand", Number(brand_id));
    }
  }, [brand_id, setValue]);

  const placeholder = isLoading
    ? "Loading..."
    : brands?.length === 0
      ? "No results"
      : "Select brand";

  return (
    <Controller
      control={control}
      name="brand"
      render={({
        field: { name, onBlur, onChange, value },
        fieldState: { error, invalid },
      }) => (
        <Select
          isInvalid={invalid}
          name={name}
          onBlur={onBlur}
          onSelectionChange={(id) => onChange(id)}
          placeholder={placeholder}
          selectedKey={value}
        >
          <Label>Choose a brand</Label>
          <div className="relative">
            <SelectButton />
            {value != null && (
              <SelectClearButton
                className="absolute inset-y-0 end-9 my-auto"
                label="Clear brand"
                onPress={() => onChange(null)}
              />
            )}
          </div>
          <FieldError>{error?.message}</FieldError>

          <SelectPopover>
            <Autocomplete filter={contains}>
              <SearchField
                aria-label="Search brands"
                autoFocus
                className="px-sm pt-xs"
              >
                <Input placeholder="Search brands..." />
              </SearchField>

              <SelectListBox
                items={brands}
                renderEmptyState={() => (
                  <Text className="p-sm text-center text-text-tertiary">
                    No brands found
                  </Text>
                )}
              >
                {(item) => (
                  <SelectListItem id={item.id}>{item.name}</SelectListItem>
                )}
              </SelectListBox>
            </Autocomplete>
          </SelectPopover>
        </Select>
      )}
    />
  );
}
